/**
 * Autenticación (SDD §8.2): Supabase Auth solo para iniciar sesión (Google y
 * enlace mágico, PKCE). La web nunca habla con la base de datos: usa el access
 * token contra la API. En modo desarrollo (sin Supabase, con VITE_DEV_TOKEN)
 * hay una sesión fija contra la API mock.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import { CONFIG, DEV_MODE } from './config'

export interface AuthSession {
  userId: string
  email: string | null
  accessToken: string
}

type Listener = (s: AuthSession | null) => void

export interface AuthClient {
  current(): AuthSession | null
  ready(): Promise<void>
  accessToken(): Promise<string | null>
  signInWithGoogle(next?: string): Promise<void>
  signInWithEmail(email: string, next?: string, captchaToken?: string): Promise<void>
  signOut(): Promise<void>
  subscribe(fn: Listener): () => void
}

/** Solo rutas internas como destino tras el login (evita redirecciones abiertas). */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return '/'
  return next
}

class DevAuth implements AuthClient {
  private session: AuthSession | null
  private listeners = new Set<Listener>()
  constructor(token: string) {
    const signedOut = localStorage.getItem('eco.dev.signedOut') === '1'
    this.session = signedOut ? null : devSession(token)
  }
  current() {
    return this.session
  }
  async ready() {}
  async accessToken() {
    return this.session?.accessToken ?? null
  }
  async signInWithGoogle(next?: string) {
    localStorage.removeItem('eco.dev.signedOut')
    this.session = devSession(CONFIG.devToken)
    this.emit()
    if (next) location.assign(safeNext(next))
  }
  async signInWithEmail(_email: string, next?: string) {
    await this.signInWithGoogle(next)
  }
  async signOut() {
    localStorage.setItem('eco.dev.signedOut', '1')
    this.session = null
    this.emit()
  }
  subscribe(fn: Listener) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  private emit() {
    for (const l of this.listeners) l(this.session)
  }
}

function devSession(token: string): AuthSession {
  return { userId: token.replace(/^test:/, ''), email: 'dev@eco.local', accessToken: token }
}

class SupabaseAuth implements AuthClient {
  private session: AuthSession | null = null
  private listeners = new Set<Listener>()
  private readonly readyPromise: Promise<void>
  private client: SupabaseClient | null = null

  constructor() {
    this.readyPromise = (async () => {
      // Build sin configurar: sin sesión y con un error claro al intentar entrar
      if (!CONFIG.supabaseUrl || !CONFIG.supabaseAnonKey) return
      const { createClient } = await import('@supabase/supabase-js')
      this.client = createClient(CONFIG.supabaseUrl, CONFIG.supabaseAnonKey, {
        auth: {
          flowType: 'pkce',
          detectSessionInUrl: true,
          persistSession: true,
          autoRefreshToken: true
        }
      })
      // Enlaces generados desde el panel de Supabase (o su API de administración) traen
      // la sesión en el fragmento (#access_token=…), no el código PKCE: se aceptan igual.
      // setSession valida el token con Supabase antes de guardarlo
      const hash = new URLSearchParams(location.hash.slice(1))
      const at = hash.get('access_token')
      const rt = hash.get('refresh_token')
      if (at && rt) {
        await this.client.auth.setSession({ access_token: at, refresh_token: rt })
        history.replaceState(null, '', location.pathname + location.search)
      }
      const { data } = await this.client.auth.getSession()
      this.set(data.session)
      this.client.auth.onAuthStateChange((_ev, s) => this.set(s))
    })().catch(() => undefined)
  }
  private async sb(): Promise<SupabaseClient> {
    await this.readyPromise
    if (!this.client)
      throw new Error(
        'El inicio de sesión no está configurado (faltan VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY).'
      )
    return this.client
  }
  private set(s: { access_token: string; user: { id: string; email?: string | null } } | null) {
    this.session = s
      ? { userId: s.user.id, email: s.user.email ?? null, accessToken: s.access_token }
      : null
    for (const l of this.listeners) l(this.session)
  }
  current() {
    return this.session
  }
  ready() {
    return this.readyPromise
  }
  async accessToken() {
    await this.readyPromise
    if (!this.client) return null
    const { data } = await this.client.auth.getSession()
    return data.session?.access_token ?? null
  }
  private redirect(next?: string) {
    return `${location.origin}/login?next=${encodeURIComponent(safeNext(next))}`
  }
  async signInWithGoogle(next?: string) {
    const { error } = await (
      await this.sb()
    ).auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: this.redirect(next) }
    })
    if (error) throw error
  }
  async signInWithEmail(email: string, next?: string, captchaToken?: string) {
    const { error } = await (
      await this.sb()
    ).auth.signInWithOtp({
      email,
      options: { emailRedirectTo: this.redirect(next), shouldCreateUser: true, captchaToken }
    })
    if (error) throw error
  }
  async signOut() {
    // global: cierra también la sesión de la extensión y otros dispositivos (SDD §6.5)
    await (await this.sb()).auth.signOut({ scope: 'global' })
  }
  subscribe(fn: Listener) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
}

export const auth: AuthClient = DEV_MODE ? new DevAuth(CONFIG.devToken) : new SupabaseAuth()
